
import { supabase } from "@/integrations/supabase/client";
import logger from "@/lib/logger";
import { toast } from "@/hooks/use-toast";
import { GenerateProposalResult } from "./proposal-types";
import { recordProposalUsage } from "./proposal-api-utils";

interface EdgeFunctionCallParams {
  jobDescription: string;
  profileTitle: string;
  profileOverview: string;
  timeoutDuration: number;
  attempt: number;
  maxRetries: number;
}

export const callProposalEdgeFunction = async ({
  jobDescription,
  profileTitle,
  profileOverview,
  timeoutDuration,
  attempt,
  maxRetries
}: EdgeFunctionCallParams): Promise<GenerateProposalResult> => {
  try {
    // Only show retry toast after first attempt
    if (attempt > 0) {
      const retryDelay = 2000 * Math.pow(2, attempt - 1); // Exponential backoff
      logger.log(`Retry attempt ${attempt} of ${maxRetries} after ${retryDelay/1000}s delay`);
      
      toast({
        title: `Retrying (${attempt}/${maxRetries})`,
        description: "Still working on your proposal...",
        duration: 3000,
      });
      
      // Wait before retry with exponential backoff
      await new Promise(resolve => setTimeout(resolve, retryDelay));
    }
    
    logger.log(`Sending proposal generation request to Edge Function (attempt ${attempt + 1})`);
    
    // Set up our own timeout mechanism
    const abortController = new AbortController();
    
    // Create timeout promise
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        abortController.abort();
        reject(new Error(`Request timed out after ${timeoutDuration/1000} seconds`));
      }, timeoutDuration);
    });
    
    // Create a promise for the actual function invocation
    const functionPromise = supabase.functions.invoke("generate-proposal", {
      body: { 
        jobDescription: jobDescription.trim(),
        profileTitle: profileTitle?.trim() || "",
        profileOverview: profileOverview?.trim() || ""
      }
    });
    
    // Race the function call against the timeout
    const result = await Promise.race([
      functionPromise,
      timeoutPromise
    ]);
    
    // Destructure the result from the function call
    const { data, error } = result;

    if (error) {
      logger.error("Error from edge function:", error);
      throw error;
    }

    // Check for errors in response data
    if (data.error) {
      logger.error("Proposal generation error from edge function:", data.error);
      
      return {
        proposal: "",
        error: data.error,
        isQuotaExceeded: !!data.quota_exceeded,
        isRateLimited: !!data.rate_limited,
        isFallback: false
      };
    }

    // Success case: got a proposal back
    if (!data.proposal) {
      throw new Error("No proposal was generated. The AI service may be experiencing issues.");
    }

    // Record successful proposal generation
    logger.log("Proposal generated successfully with length:", data.proposal.length);
    await recordProposalUsage(jobDescription.trim().length);

    return {
      proposal: data.proposal,
      error: null,
      isQuotaExceeded: false,
      isRateLimited: false,
      isFallback: !!data.is_fallback
    };
  } catch (error) {
    logger.error("Error calling edge function:", error);
    throw error;
  }
};
